import { Blueprint } from '../../blueprint.class';
import { Countertop } from '../../../countertops/countertop.class';
import { Island } from '../../../islands/island.class';
import { ToolResult } from '../../types';
import { buildKitchenShapeCfn, getAvailableShapeKeys } from '../../../kitchens/functions/build-kitchen-shape.cfn';

export type SetKitchenShapeArgs = {
	shapeKey: string;
	confirmed?: boolean;
};

export const setKitchenShapeTool = (blueprint: Blueprint, { shapeKey, confirmed }: SetKitchenShapeArgs): ToolResult => {
	if (!shapeKey) {
		return { success: false, error: 'Missing shapeKey. Use get_layout_options to get valid shape keys.' };
	}

	const validKeys = getAvailableShapeKeys();
	if (!validKeys.includes(shapeKey)) {
		return { success: false, error: `Invalid shapeKey '${shapeKey}'. Valid keys: ${validKeys.join(', ')}` };
	}

	const kitchen = blueprint.kitchens.getActiveVersion();
	if (!kitchen) {
		return { success: false, error: 'No active kitchen found.' };
	}

	// Check if appliances exist on current islands
	const { numAppliances } = kitchen.design.islands.groupAppliances();
	if (numAppliances > 0 && !confirmed) {
		return { success: false, error: `The current kitchen has ${numAppliances} appliance(s) placed on the islands. Changing the shape will remove all existing appliances. Call set_kitchen_shape again with confirmed: true to proceed.` };
	}

	// Build the shape on a temporary blueprint (proven path),
	// serialize, then deserialize into the existing kitchen's design
	// so external references (e.g. 3D controller) remain valid.
	const tempBp = new Blueprint({ userEmail: blueprint.context.userEmail, dataIn: { email: blueprint.context.userEmail } });
	const tempKitchen = tempBp.kitchens.getActiveVersion();

	const success = buildKitchenShapeCfn(tempKitchen, shapeKey);
	if (!success) {
		return { success: false, error: `Failed to build kitchen shape '${shapeKey}'.` };
	}

	// Serialize the fresh design
	const freshDesign = tempKitchen.design.serialize();
	const { islands, countertops } = kitchen.design;

	// Capture old island IDs for 3D removal transitions
	const oldIslandIds = islands.children.map((i) => i.id);

	// Clear existing children
	islands.children.splice(0, islands.children.length);
	countertops.children.splice(0, countertops.children.length);

	// Publish removal events so the 3D scene runs remove transitions
	for (const id of oldIslandIds) {
		islands.publish('islands', 'deleted', { ref: id, old: null, new: [] });
	}

	// Reconstruct islands from serialized data with proper parent context
	if (freshDesign.islands?.children) {
		freshDesign.islands.children.forEach((islandData) => {
			islands.children.push(new Island({ dataIn: islandData, parent: islands }));
		});
	}
	if (freshDesign.islands?.defaultIslandId) {
		islands.setDefaultIsland(freshDesign.islands.defaultIslandId);
	}

	// Reconstruct countertops from serialized data
	if (freshDesign.countertops?.children) {
		freshDesign.countertops.children.forEach((ctData) => {
			countertops.children.push(new Countertop({ dataIn: ctData, parent: countertops }));
		});
	}

	// Publish addition events so the 3D scene builds + fades in new islands
	for (const island of islands.children) {
		islands.publish('islands', 'added', { ref: island.id, old: null, new: [] });
	}

	kitchen.description = tempKitchen.description;

	return {
		success: true,
		message: `Set kitchen shape to '${kitchen.description}' (kitchen ID: ${kitchen.id}).`
	};
};
